import React from 'react'
import {
  Timeline,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
} from '@mui/lab'
import { Box, SvgIcon, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ComponentMap, { IComponentMap } from '../ComponentMap'
import { TimelineComponentProps } from './Timeline.types'
import logo from '../static/Tdc-simple-logo.png'
import Arrow from '../static/Arrow.svg'
import CardComponent, { CardProps } from '../Card'
import { ChipProps } from '../Chip'

enum hideOnMobile {
  LEFT = 'Left item',
  RIGHT = 'Right item',
}
enum addArrowOnItem {
  LEFT = 'Left item',
  RIGHT = 'Right item',
}

const TimelineComponent: React.FC<TimelineComponentProps> = ({ timelineItems, showStepper, chip }) => {
  const { breakpoints, palette } = useTheme()
  const isMobile = useMediaQuery(breakpoints.down('sm'))
  const styles = {
    stepperBox: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '55px',
      height: '55px',
      borderRadius: '50%',
      backgroundColor: '#3f51b5',
      backgroundImage: `url(${logo})`,
      backgroundSize: 'cover',
      color: palette.common.white,
      fontWeight: 'bold',
      fontSize: '25px',
      [breakpoints.down('sm')]: {
        width: '30px',
        height: '30px',
        fontSize: '21px',
      },
    },
    root: {
      paddingLeft: '0px !important',
      '& .MuiTimelineItem-root ': {
        marginBottom: 0,
        '&::before': {
          content: 'none',
          padding: 0,
        },
      },
      '& .MuiTimelineOppositeContent-root': {
        '& .MuiBox-root': {
          display: 'block',
          textAlign: 'left',
        },
      },
      [breakpoints.down('sm')]: {
        '& .MuiTimelineContent-root': {
          padding: '6px 0',
        },
      },
    },
    timelineItem: {
      columnGap: '20px',
      [breakpoints.down('sm')]: {
        columnGap: '2px',
      },
    },
    timelineopposite: {
      padding: '65px 0',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      '& .gatsby-image-wrapper img': {
        width: '100% !important',
      },
      'svg': {
        margin: '0 auto',
      },
      '& div': { justifyContent: 'center' },
    },
    box: {
      textAlign: 'center',
      marginTop: '15px',
    },
    svg: {
      cursor: 'pointer',
      width: '40px',
      height: '40px',
      margin: '0 auto',
    },
  }

  const StepCircle = ({ step, showStep }: { step: number; showStep: boolean }) => {
    return <Box sx={styles.stepperBox}>{showStep ? step : ''}</Box>
  }

  if (chip && chip.body && chip.body.references) {
    chip?.body?.references.map((item, i) => {
      ;(item as unknown as ChipProps).link = `#timeline-item-${i}`
    })
  }

  return (
    <>
      {chip && <CardComponent {...(chip as CardProps)} />}
      <Timeline sx={styles.root}>
        {timelineItems?.map((item, index) => {
          const leftColumnContent = item?.columnContent?.[0]
          const rightColumnContent = item?.columnContent?.[1]

          const scrollToNextElement = (nextIndex: number) => {
            const nextTimelineItem = timelineItems[nextIndex]
            if (!nextTimelineItem) return
            const nextItem = document.querySelector(`[data-scroll-id="timeline-item-${nextIndex}"]`)
            if (!nextItem) return
            nextItem.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }

          return (
            <TimelineItem key={item?.contentful_id} sx={styles.timelineItem}>
              {isMobile ? (
                <>
                  <TimelineSeparator data-scroll-id={`timeline-item-${index}`}>
                    <StepCircle step={index + 1} showStep={showStepper || false} />
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent>
                    <Box display="flex" flexDirection="column">
                      {item?.hideOnMobile !== hideOnMobile.LEFT && (
                        <ComponentMap {...(leftColumnContent as IComponentMap)} />
                      )}
                      {item?.hideOnMobile !== hideOnMobile.RIGHT && (
                        <ComponentMap {...(rightColumnContent as IComponentMap)} />
                      )}
                      {item?.addArrowOnItem && (
                        <SvgIcon
                          component={Arrow}
                          viewBox="0 0 48 48"
                          sx={styles.svg}
                          onClick={() => scrollToNextElement(index + 1)}
                        />
                      )}
                    </Box>
                  </TimelineContent>
                </>
              ) : (
                <>
                  <TimelineOppositeContent sx={[styles.timelineopposite, { alignItems: 'flex-end' }]}>
                    {leftColumnContent && <ComponentMap {...(leftColumnContent as IComponentMap)} />}
                    {item?.addArrowOnItem === addArrowOnItem.LEFT && (
                      <SvgIcon
                        component={Arrow}
                        viewBox="0 0 48 48"
                        sx={styles.svg}
                        onClick={() => scrollToNextElement(index + 1)}
                      />
                    )}
                  </TimelineOppositeContent>

                  <TimelineSeparator data-scroll-id={`timeline-item-${index}`}>
                    <TimelineConnector />
                    <StepCircle step={index + 1} showStep={showStepper || false} />
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent sx={styles.timelineopposite}>
                    {rightColumnContent && <ComponentMap {...(rightColumnContent as IComponentMap)} />}
                    {item?.addArrowOnItem === addArrowOnItem.RIGHT && (
                      <Box sx={styles.box}>
                        <SvgIcon
                          component={Arrow}
                          viewBox="0 0 48 48"
                          sx={styles.svg}
                          onClick={() => scrollToNextElement(index + 1)}
                        />
                      </Box>
                    )}
                  </TimelineContent>
                </>
              )}
            </TimelineItem>
          )
        })}
      </Timeline>
    </>
  )
}

export default TimelineComponent
